import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Building2, Calendar, ChevronDown, ChevronLeft, ChevronRight, Search, User } from 'lucide-react'
import api from '../../Includes/api/api'

const INTEREST_OPTIONS = [
  { value: '', label: 'All interests' },
  { value: 'interested', label: 'Interested' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'pending', label: 'Pending' },
  { value: 'followup', label: 'Follow-up' },
  { value: 'go_to_main_branch', label: 'Go to main branch' },
]

const SUMMARY_INTERESTS = [
  { value: 'interested', label: 'Interested', color: 'green' },
  { value: 'followup', label: 'Follow-up', color: 'blue' },
  { value: 'go_to_main_branch', label: 'Go to main branch', color: 'orange' },
  { value: 'not_interested', label: 'Not interested', color: 'red' },
  { value: 'pending', label: 'Pending', color: 'orange' },
]

const schoolLabel = (areaName) => (areaName || '').split(',')[0].trim() || 'Unknown'

const interestLabel = (status) => {
  if (!status || status === 'pending') return 'Pending'
  return String(status).replace(/_/g, ' ')
}

const formatDate = (value) => {
  if (!value) return ''
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
  const parsed = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const quoteLabel = (status) => {
  if (status === 'generated') return 'Quotation generated'
  if (status === 'sent') return 'Quotation sent'
  if (status === 'reviewed') return 'Final review'
  return '—'
}

const isoDate = (year, month, day) => {
  const m = String(month).padStart(2, '0')
  const d = String(day).padStart(2, '0')
  return `${year}-${m}-${d}`
}

const academicYearDateRange = (name) => {
  const match = String(name || '').match(/(\d{4})\s*[-–—]\s*(\d{2,4})/)
  if (!match) {
    const end = new Date()
    const start = new Date(end)
    start.setFullYear(start.getFullYear() - 1)
    start.setDate(start.getDate() + 1)
    return {
      from: isoDate(start.getFullYear(), start.getMonth() + 1, start.getDate()),
      to: isoDate(end.getFullYear(), end.getMonth() + 1, end.getDate()),
    }
  }
  const startYear = Number(match[1])
  const endPart = match[2]
  const endYear = endPart.length === 4
    ? Number(endPart)
    : Number(String(startYear).slice(0, 2) + endPart)
  return {
    from: isoDate(startYear, 4, 1),
    to: isoDate(endYear, 3, 31),
  }
}

export default function SalesTrack() {
  const location = useLocation()
  const mineOnly = location.pathname === '/sales/my-track'
  const [schools, setSchools] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [interest, setInterest] = useState('')
  const [staffId, setStaffId] = useState('')
  const [staff, setStaff] = useState([])
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [openSchool, setOpenSchool] = useState('')
  const [yearName, setYearName] = useState('')
  const [academicYearId, setAcademicYearId] = useState('')
  const [yearReady, setYearReady] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [summary, setSummary] = useState(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    if (mineOnly) return undefined
    let mounted = true
    const loadStaff = async () => {
      try {
        const res = await api.get('/persons/')
        const raw = res.data
        const rows = Array.isArray(raw?.data) ? raw.data
          : Array.isArray(raw?.results) ? raw.results
            : Array.isArray(raw) ? raw : []
        if (mounted) {
          setStaff([...rows].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))))
        }
      } catch {
        if (mounted) setStaff([])
      }
    }
    loadStaff()
    return () => { mounted = false }
  }, [mineOnly])

  useEffect(() => {
    let mounted = true
    const loadYear = async () => {
      try {
        const yearsRes = await api.get('/basic/academicyear/')
        const rawYears = yearsRes.data
        const years = Array.isArray(rawYears?.data) ? rawYears.data
          : Array.isArray(rawYears?.results) ? rawYears.results
            : Array.isArray(rawYears) ? rawYears : []
        const activeYear = years.find((year) => year.is_active) || years[0]
        if (mounted) {
          setYearName(activeYear?.name || '')
          setAcademicYearId(activeYear?.id ? String(activeYear.id) : '')
          const range = academicYearDateRange(activeYear?.name)
          setDateFrom(range.from)
          setDateTo(range.to)
        }
      } catch {
        if (mounted) {
          setYearName('')
          const range = academicYearDateRange('')
          setDateFrom(range.from)
          setDateTo(range.to)
        }
      } finally {
        if (mounted) setYearReady(true)
      }
    }
    loadYear()
    return () => { mounted = false }
  }, [])

  useEffect(() => {
    if (!yearReady) return undefined
    let mounted = true
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const params = { page, page_size: pageSize }
        if (mineOnly) params.mine = '1'
        else if (staffId) params.person_id = staffId
        if (academicYearId) params.academic_year_id = academicYearId
        if (debouncedQuery) params.q = debouncedQuery
        if (interest) params.interest = interest
        if (dateFrom) params.interest_date_from = dateFrom
        if (dateTo) params.interest_date_to = dateTo
        const res = await api.get('/visits/school-track/', { params })
        if (!mounted) return
        setSchools(res.data?.data || [])
        setTotal(res.data?.total_schools || 0)
        setTotalPages(res.data?.total_pages || 0)
        setSummary(res.data?.summary || null)
        if (res.data?.page && res.data.page !== page) setPage(res.data.page)
      } catch (err) {
        if (mounted) {
          setError(err.response?.data?.error || 'Could not load school visits.')
          setSchools([])
          setTotal(0)
          setTotalPages(0)
          setSummary(null)
        }
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => { mounted = false }
  }, [yearReady, academicYearId, debouncedQuery, interest, staffId, dateFrom, dateTo, page, pageSize, mineOnly])

  const resetPage = () => setPage(1)

  return (
    <div className="visits-page visits-page--overview" style={{ padding: 16 }}>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div>
          <h1 className="page-title">{mineOnly ? 'My Sales Track' : 'Sales Track'}</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 13 }}>
            {mineOnly ? 'Your school visits' : 'School-wise visits'}{yearName ? ` · ${yearName}` : ''}. Interest uses the latest completed visit.
          </p>
        </div>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12 }}>
          <label className="form-group" style={{ marginBottom: 0, minWidth: 220, flex: '1 1 220px' }}>
            <span className="form-label">Search</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Search size={16} />
              <input
                className="form-control"
                placeholder="School or visitor"
                value={query}
                onChange={(e) => { setQuery(e.target.value); resetPage() }}
              />
            </span>
          </label>
          <label className="form-group" style={{ marginBottom: 0, minWidth: 180 }}>
            <span className="form-label">Last visit interest</span>
            <select
              className="form-control"
              value={interest}
              onChange={(e) => { setInterest(e.target.value); resetPage() }}
            >
              {INTEREST_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          {!mineOnly && (
            <label className="form-group" style={{ marginBottom: 0, minWidth: 180 }}>
              <span className="form-label">Staff</span>
              <select
                className="form-control"
                value={staffId}
                onChange={(e) => { setStaffId(e.target.value); resetPage() }}
              >
                <option value="">All staff</option>
                {staff.map((person) => (
                  <option key={person.id} value={person.id}>{person.name}</option>
                ))}
              </select>
            </label>
          )}
          <label className="form-group" style={{ marginBottom: 0 }}>
            <span className="form-label">Interest date from</span>
            <input
              type="date"
              className="form-control"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => { setDateFrom(e.target.value); resetPage() }}
            />
          </label>
          <label className="form-group" style={{ marginBottom: 0 }}>
            <span className="form-label">Interest date to</span>
            <input
              type="date"
              className="form-control"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => { setDateTo(e.target.value); resetPage() }}
            />
          </label>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)', paddingBottom: 8 }}>
            {total} school{total === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {!loading && summary && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div>
                <div className="stat-title">Schools</div>
                <div className="stat-value blue">{summary.total_schools}</div>
              </div>
            </div>
            <div className="stat-card">
              <div>
                <div className="stat-title">Visits</div>
                <div className="stat-value orange">{summary.total_visits}</div>
              </div>
            </div>
            {!mineOnly && (
              <div className="stat-card">
                <div>
                  <div className="stat-title">Staff</div>
                  <div className="stat-value blue">{summary.total_staff}</div>
                </div>
              </div>
            )}
            {SUMMARY_INTERESTS.map((item) => (
              <div
                key={item.value}
                className="stat-card"
                style={{ cursor: 'pointer', outline: interest === item.value ? '2px solid var(--accent-blue)' : 'none' }}
                onClick={() => { setInterest(interest === item.value ? '' : item.value); resetPage() }}
              >
                <div>
                  <div className="stat-title">{item.label}</div>
                  <div className={`stat-value ${item.color}`}>{summary.interest?.[item.value] || 0}</div>
                </div>
              </div>
            ))}
          </div>

          {!mineOnly && (summary.staff || []).length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <h2 className="card-title"><User size={18} /> Staff-wise summary</h2>
              </div>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Staff</th>
                      <th>Schools</th>
                      <th>Visits</th>
                      <th>Completed</th>
                      {SUMMARY_INTERESTS.map((item) => <th key={item.value}>{item.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {summary.staff.map((row) => (
                      <tr
                        key={row.person_id}
                        style={{ cursor: 'pointer', background: String(row.person_id) === String(staffId) ? 'var(--bg-secondary, #f1f5f9)' : undefined }}
                        onClick={() => { setStaffId(String(row.person_id) === String(staffId) ? '' : String(row.person_id)); resetPage() }}
                      >
                        <td>
                          <span style={{ fontWeight: 600 }}>{row.name || '—'}</span>
                          {row.designation && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.designation}</div>
                          )}
                        </td>
                        <td>{row.schools}</td>
                        <td>{row.visits}</td>
                        <td>{row.completed_visits}</td>
                        {SUMMARY_INTERESTS.map((item) => (
                          <td key={item.value}>{row.interest?.[item.value] || 0}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {error && <p style={{ color: '#b91c1c' }}>{error}</p>}
      {loading ? (
        <p>Loading schools…</p>
      ) : schools.length === 0 ? (
        <div className="empty-state">
          <Building2 size={40} />
          <h3>No school visits</h3>
          <p>No schools match this interest or last-visit date range.</p>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 10 }}>
            {schools.map((row) => {
              const open = openSchool === row.school
              const lastDate = formatDate(row.last_interest_date)
              return (
                <div key={row.school} className="card" style={{ overflow: 'hidden', margin: 0 }}>
                  <button
                    type="button"
                    onClick={() => setOpenSchool(open ? '' : row.school)}
                    style={{
                      width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
                      padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    }}
                  >
                    {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <Building2 size={16} />
                    <strong style={{ flex: 1, minWidth: 160 }}>{schoolLabel(row.school)}</strong>
                    <span className="chip chip-slate" style={{ textTransform: 'capitalize' }}>
                      {interestLabel(row.last_interest_status)}
                    </span>
                    {lastDate && (
                      <span className="chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Calendar size={12} /> {lastDate}
                      </span>
                    )}
                    <span className="chip chip-slate">{row.visit_count} visit{row.visit_count === 1 ? '' : 's'}</span>
                    <span className="chip">{(row.visitors || []).length} visitor{(row.visitors || []).length === 1 ? '' : 's'}</span>
                  </button>
                  {open && (
                    <div className="table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Who visited</th>
                            <th>Planned date</th>
                            <th>Visited date</th>
                            <th>Visit status</th>
                            <th>Interest</th>
                            <th>Quotation</th>
                            <th>Review</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(row.visits || []).map((visit) => (
                            <tr key={visit.id}>
                              <td>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                                  <User size={13} /> {visit.visitor || '—'}
                                </span>
                                {visit.designation && (
                                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 19 }}>{visit.designation}</div>
                                )}
                              </td>
                              <td>
                                {visit.planned_date ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <Calendar size={13} />
                                    {formatDate(visit.planned_date)}
                                  </span>
                                ) : '—'}
                              </td>
                              <td>
                                {visit.visited_date ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <Calendar size={13} />
                                    {formatDate(visit.visited_date)}
                                  </span>
                                ) : '—'}
                              </td>
                              <td style={{ textTransform: 'capitalize' }}>{String(visit.status || '').replace(/_/g, ' ') || '—'}</td>
                              <td style={{ textTransform: 'capitalize' }}>{interestLabel(visit.interest_status)}</td>
                              <td>{quoteLabel(visit.quotation_status)}</td>
                              <td style={{ maxWidth: 240 }}>{visit.final_review || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          {totalPages > 0 && (
            <div className="pagination-bar">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: 'var(--text-secondary)' }}>Rows per page:</span>
                <select
                  className="form-control"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); resetPage() }}
                  style={{ width: 'auto', border: '1px solid var(--border-color)', padding: '4px 8px', height: 32, fontSize: 13 }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                  Page {page} of {totalPages} · {total} total
                </span>
                <button type="button" className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
                  <ChevronLeft size={14} />
                </button>
                <button type="button" className="btn btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)}>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
